import { useEffect, useState, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatRp, formatDateID, todayWITA, LOCATIONS, PAYMENT_STATUSES, ALL_SERVICES } from "@/lib/constants";
import TransactionDetail from "@/components/TransactionDetail";
import Receipt from "@/components/Receipt";
import OmzetDrilldown from "@/components/OmzetDrilldown";
import { OmzetTrendChart, PetugasBarChart } from "@/components/OmzetCharts";
import {
  ChartLineUp, Wallet, Receipt as ReceiptIcon, Tag, Warning, House, Storefront, MagnifyingGlass, FileCsv, FileXls, FilePdf, Printer, CaretDown, ArrowUUpLeft, Pill,
} from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

function firstOfMonth() { const t = todayWITA(); return t.slice(0, 8) + "01"; }
function addDays(iso, n) { const [y, m, d] = iso.split("-").map(Number); const dt = new Date(y, m - 1, d + n); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`; }

const PRESETS = {
  "Hari Ini": () => [todayWITA(), todayWITA()],
  "Kemarin": () => [addDays(todayWITA(), -1), addDays(todayWITA(), -1)],
  "Minggu Ini": () => [addDays(todayWITA(), -6), todayWITA()],
  "Bulan Ini": () => [firstOfMonth(), todayWITA()],
};

export default function OmzetDashboard() {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayWITA());
  const [staff, setStaff] = useState([]);
  const [units, setUnits] = useState([]);
  const [filters, setFilters] = useState({ petugas: "", jenis_layanan: "", lokasi: "", status_pembayaran: "", unit: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [drill, setDrill] = useState(null);
  const [denied, setDenied] = useState(false);

  const openDetailById = async (rid) => {
    if (!rid) return;
    try { const r = await api.get(`/reservations/${rid}`); setDetail(r.data); }
    catch { toast.error("Detail reservasi tidak ditemukan"); }
  };

  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); api.get("/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { start, end };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try { const r = await api.get("/omzet", { params }); setData(r.data); setDenied(false); }
    catch (e) { if (e?.response?.status === 403) setDenied(true); else toast.error("Gagal memuat laporan omzet"); }
    finally { setLoading(false); }
  }, [start, end, filters]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (name) => { const [s, e] = PRESETS[name](); setStart(s); setEnd(e); };
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportCSV = () => {
    if (!data) return;
    const header = ["Tanggal", "Nama Pasien", "Layanan", "Petugas", "Lokasi", "Harga Normal", "Diskon", "Harga Final", "Metode Bayar", "Status Bayar"];
    const rows = data.items.map((d) => [
      formatDateID(d.tanggal_layanan || d.tanggal_kunjungan), d.nama_lengkap, d.jenis_layanan, d.petugas_nama || "", d.lokasi || "",
      d.harga_normal || 0, (d.jenis_diskon === "Persentase" ? d.nilai_diskon + "%" : d.nilai_diskon || 0), d.harga_final || 0, d.metode_pembayaran || "", d.status_pembayaran || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Omzet_${start}_${end}.csv`; a.click();
  };

  const downloadExport = async (fmt) => {
    const params = { start, end, format: fmt };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const r = await api.get("/export/omzet", { params, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data);
      a.download = `Omzet_${start}_${end}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Laporan omzet berhasil diunduh");
    } catch (e) { toast.error("Gagal mengekspor laporan"); }
  };

  const S = data?.summary;
  const CARDS = S ? [
    { key: "nilai_tagihan", label: "Nilai Tagihan", value: formatRp(S.nilai_tagihan), raw: S.nilai_tagihan, icon: ReceiptIcon, color: "#6A38C7", bg: "#EEE6FF" },
    { key: "pendapatan_diterima", label: "Pendapatan Layanan (Diterima)", value: formatRp(S.pendapatan_diterima), raw: S.pendapatan_diterima, icon: Wallet, color: "#047857", bg: "#d1fae5" },
    { key: "pendapatan_farmasi", label: "Pendapatan Farmasi (Obat)", value: formatRp(S.pendapatan_farmasi || 0), raw: S.pendapatan_farmasi || 0, icon: Pill, color: "#be185d", bg: "#fce7f3" },
    { key: "total_klinik", label: "Total Pendapatan Klinik", value: formatRp(S.total_pendapatan_klinik || 0), raw: S.total_pendapatan_klinik || 0, icon: Wallet, color: "#4C1D95", bg: "#ede9fe" },
    { key: "tunai_perlu_rekonsiliasi", label: "Tunai/Manual Perlu Rekonsiliasi Kas", value: formatRp(S.tunai_perlu_rekonsiliasi), raw: S.tunai_perlu_rekonsiliasi, icon: Warning, color: "#b45309", bg: "#fef3c7" },
    { key: "refund", label: "Refund", value: formatRp(S.refund), raw: S.refund, icon: ArrowUUpLeft, color: "#be123c", bg: "#ffe4e6" },
    { key: "total_transaksi", label: "Total Transaksi", value: S.total_transaksi, raw: S.total_transaksi, isCount: true, icon: Tag, color: "#7C4DE0", bg: "#f3e8ff" },
    { key: "belum_bayar", label: "Belum Bayar", value: formatRp(S.total_belum_bayar), raw: S.total_belum_bayar, icon: Warning, color: "#0891b2", bg: "#cffafe" },
  ] : [];

  const drillParams = { start, end, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };

  if (denied) {
    return (
      <div data-testid="omzet-denied" className="mx-auto mt-10 max-w-md rounded-2xl border border-rose-100 bg-white p-6 text-center">
        <Warning size={32} weight="duotone" className="mx-auto text-amber-500" />
        <p className="mt-2 font-heading text-lg font-extrabold text-slate-800">Akses Omzet Terbatas</p>
        <p className="mt-1 text-sm text-slate-500">Laporan Omzet & Kinerja hanya tersedia untuk Super Admin dan petugas (data sendiri). Hubungi Super Admin bila Anda memerlukan akses.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-up space-y-6">
      <div className="no-print flex items-center gap-2">
        <ChartLineUp size={26} weight="duotone" className="text-[#7C4DE0]" />
        <h1 className="font-heading text-3xl font-extrabold text-slate-800">Omzet & Kinerja Petugas</h1>
      </div>

      {/* Filters */}
      <div className="no-print rounded-2xl border border-rose-100/70 bg-white p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {Object.keys(PRESETS).map((p) => (
            <button key={p} data-testid={`preset-${p}`} onClick={() => applyPreset(p)} className="rounded-full bg-rose-50 px-3 py-1.5 text-xs font-semibold text-[#6A38C7] hover:bg-[#EEE6FF]">{p}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Dari</label><input type="date" data-testid="omzet-start" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Sampai</label><input type="date" data-testid="omzet-end" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} /></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Petugas</label><select data-testid="omzet-petugas" value={filters.petugas} onChange={(e) => setF("petugas", e.target.value)} className={inputCls}><option value="">Semua</option>{staff.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}</select></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Layanan</label><select data-testid="omzet-layanan" value={filters.jenis_layanan} onChange={(e) => setF("jenis_layanan", e.target.value)} className={inputCls}><option value="">Semua</option>{ALL_SERVICES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Lokasi</label><select data-testid="omzet-lokasi" value={filters.lokasi} onChange={(e) => setF("lokasi", e.target.value)} className={inputCls}><option value="">Semua</option>{LOCATIONS.map((l) => <option key={l}>{l}</option>)}</select></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Poli / Unit</label><select data-testid="omzet-unit" value={filters.unit} onChange={(e) => setF("unit", e.target.value)} className={inputCls}><option value="">Semua Unit</option>{units.filter((u) => u.aktif).map((u) => <option key={u.id} value={u.id}>{u.nama}</option>)}<option value="unmapped">Belum Dipetakan</option></select></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Status Bayar</label><select data-testid="omzet-status" value={filters.status_pembayaran} onChange={(e) => setF("status_pembayaran", e.target.value)} className={inputCls}><option value="">Semua</option>{PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="omzet-export" onClick={exportCSV} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"><FileCsv size={16} weight="duotone" /> CSV</button>
          <button data-testid="omzet-export-xlsx" onClick={() => downloadExport("xlsx")} className="flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"><FileXls size={16} weight="duotone" /> Excel</button>
          <button data-testid="omzet-export-pdf" onClick={() => downloadExport("pdf")} className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700"><FilePdf size={16} weight="duotone" /> PDF</button>
          <button data-testid="omzet-print" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-900"><Printer size={16} weight="duotone" /> Print</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" /></div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {CARDS.map((c) => (
              <button key={c.label} data-testid={`omzet-card-${c.label}`} onClick={() => setDrill(c)} className="rounded-2xl border border-rose-100/70 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#6A38C7]/40 hover:shadow-md">
                <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: c.bg, color: c.color }}><c.icon size={20} weight="duotone" /></span>
                <p className="font-heading text-lg font-extrabold leading-tight text-slate-800">{c.value}</p>
                <p className="mt-0.5 text-xs text-slate-500">{c.label}</p>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <OmzetTrendChart trend={data.trend} />
            <PetugasBarChart perPetugas={data.per_petugas} />
          </div>

          <div>
            <h2 className="mb-3 font-heading text-xl font-bold text-slate-800">Omzet per Petugas</h2>
            <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-rose-100 bg-[#F7F0FF] text-left text-xs font-bold uppercase text-slate-500">
                  <th className="px-3 py-3">Petugas</th><th className="px-3 py-3">Pasien</th><th className="px-3 py-3">Layanan</th><th className="px-3 py-3">Omzet Kotor</th><th className="px-3 py-3">Diskon</th><th className="px-3 py-3">Omzet Bersih</th><th className="px-3 py-3">Komisi</th><th className="px-3 py-3">Target</th>
                </tr></thead>
                <tbody>
                  {data.per_petugas.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Belum ada transaksi pada periode ini.</td></tr>
                  ) : data.per_petugas.map((p) => (
                    <Fragment key={p.petugas}>
                      <tr data-testid={`petugas-row-${p.petugas}`} onClick={() => setExpanded(expanded === p.petugas ? null : p.petugas)} className="cursor-pointer border-b border-rose-50 hover:bg-rose-50/40">
                        <td className="px-3 py-3 font-semibold text-slate-800"><span className="flex items-center gap-1"><CaretDown size={14} className={`transition-transform ${expanded === p.petugas ? "rotate-180" : ""}`} />{p.petugas}</span></td>
                        <td className="px-3 py-3">{p.jumlah_pasien}</td>
                        <td className="px-3 py-3">{p.jumlah_layanan}</td>
                        <td className="px-3 py-3">{formatRp(p.omzet_kotor)}</td>
                        <td className="px-3 py-3 text-amber-700">{formatRp(p.total_diskon)}</td>
                        <td className="px-3 py-3 font-bold text-emerald-700">{formatRp(p.omzet_bersih)}</td>
                        <td className="px-3 py-3 text-[#6A38C7]">{p.komisi_persen ? `${formatRp(p.komisi)} (${p.komisi_persen}%)` : "-"}</td>
                        <td className="px-3 py-3">{p.target_bulanan ? <span className={p.pencapaian >= 100 ? "font-bold text-emerald-700" : "text-slate-600"}>{p.pencapaian}%</span> : "-"}</td>
                      </tr>
                      {expanded === p.petugas && (
                        <tr><td colSpan={8} className="bg-rose-50/30 px-3 py-2">
                          <div className="space-y-1">
                            {data.items.filter((i) => (i.petugas_nama || "Tanpa Petugas") === p.petugas).map((i) => (
                              <div key={i.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-xs">
                                <button data-testid={`tx-${i.id}`} onClick={() => setDetail(i)} className="flex flex-1 items-center justify-between gap-2 text-left hover:text-[#6A38C7]">
                                  <span className="font-semibold text-slate-700">{formatDateID(i.tanggal_layanan || i.tanggal_kunjungan)} · {i.nama_lengkap}</span>
                                  <span className="text-slate-500">{i.jenis_layanan}</span>
                                  <span className="font-bold text-[#6A38C7]">{formatRp(i.harga_final)}</span>
                                </button>
                                <button data-testid={`receipt-btn-${i.id}`} onClick={() => setReceipt(i)} className="rounded-lg bg-[#EEE6FF] px-2.5 py-1 font-semibold text-[#6A38C7]">Kwitansi</button>
                              </div>
                            ))}
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      <TransactionDetail tx={detail} open={!!detail} onClose={() => setDetail(null)} />
      <Receipt tx={receipt} open={!!receipt} onClose={() => setReceipt(null)} />
      <OmzetDrilldown open={!!drill} card={drill?.key} cardLabel={drill?.label} cardValue={drill?.raw}
        isCount={drill?.isCount} params={drillParams} onClose={() => setDrill(null)}
        onRowClick={(r) => openDetailById(r.link_id)} />
    </div>
  );
}
